import React from 'react';
import { ArrowRight, Star, Shield, CheckCircle2, TrendingUp, Award } from 'lucide-react';
import { Button } from './ui/Button';
import { useTranslations } from '../hooks/useTranslations';
export function Hero() {
  const {
    t
  } = useTranslations();
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    contactSection?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-white" data-id="element-188">
      {/* Background Pattern */}
      <div className="absolute inset-0 z-0 opacity-[0.03]" style={{
      backgroundImage: 'radial-gradient(#8B5CF6 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-189" />

      {/* Gradient Orbs */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-purple-400/20 to-fuchsia-400/20 rounded-full blur-3xl -z-10" data-id="element-190" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-purple-400/10 to-fuchsia-400/10 rounded-full blur-3xl -z-10" data-id="element-191" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-192">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-193">
          {/* Left Content */}
          <div className="space-y-8 text-center lg:text-left" data-id="element-194">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-purple-50 border border-purple-100 text-purple-600 text-sm font-medium" data-id="element-195">
              <span className="flex h-2 w-2 rounded-full bg-gradient-to-r from-purple-500 to-fuchsia-500 mr-2 animate-pulse" data-id="element-196"></span>
              {t('hero.badge')}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-197">
              {t('hero.title')}
            </h1>

            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-198">
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4" data-id="element-199">
              <Button size="lg" className="group" onClick={scrollToContact} data-id="element-200">
                {t('hero.cta')}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-201" />
              </Button>
              <div className="flex items-center text-sm font-medium text-gray-600" data-id="element-202">
                <div className="flex -space-x-2 mr-3" data-id="element-203">
                  {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 overflow-hidden" data-id="element-204">
                      <img src={`https://i.pravatar.cc/100?img=${i + 20}`} alt="Customer" className="w-full h-full object-cover" data-id="element-205" />
                    </div>)}
                </div>
                {t('hero.trusted')}
              </div>
            </div>

            <div className="pt-4 flex items-center justify-center lg:justify-start gap-6" data-id="element-206">
              <div className="flex items-center gap-2" data-id="element-207">
                <div className="flex text-purple-500" data-id="element-208">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-5 h-5 fill-current" data-id="element-209" />)}
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-210">
                  {t('hero.trustpilot')}
                </span>
              </div>
            </div>
          </div>

          {/* Right Content - Realistic Dashboard Mockup */}
          <div className="relative lg:h-[600px] flex items-center justify-center" data-id="element-211">
            <div className="relative w-full max-w-lg" data-id="element-212">
              {/* Main Dashboard Card - Glassmorphism */}
              <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/20 overflow-hidden" data-id="element-213">
                {/* Gradient Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-fuchsia-500/5 to-transparent" data-id="element-214" />

                <div className="relative z-10" data-id="element-215">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-8" data-id="element-216">
                    <div className="flex items-center gap-3" data-id="element-217">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center" data-id="element-218">
                        <Shield className="w-6 h-6 text-white" data-id="element-219" />
                      </div>
                      <div data-id="element-220">
                        <p className="text-xs text-gray-500 font-medium" data-id="element-221">
                          {t('hero.dashboard.status')}
                        </p>
                        <p className="text-sm font-bold text-purple-600" data-id="element-222">
                          {t('hero.dashboard.protected')}
                        </p>
                      </div>
                    </div>
                    <div className="px-3 py-1 bg-green-100 rounded-full" data-id="element-223">
                      <span className="text-xs font-bold text-green-700" data-id="element-224">
                        {t('hero.dashboard.active')}
                      </span>
                    </div>
                  </div>

                  {/* Large FICO Score */}
                  <div className="text-center mb-8 py-6 bg-gradient-to-br from-purple-50 to-fuchsia-50 rounded-2xl border border-purple-100" data-id="element-225">
                    <p className="text-sm text-gray-600 font-medium mb-2" data-id="element-226">
                      {t('hero.dashboard.yourScore')}
                    </p>
                    <div className="text-7xl font-bold bg-gradient-to-r from-purple-600 to-fuchsia-500 bg-clip-text text-transparent mb-2" data-id="element-227">
                      820
                    </div>
                    <div className="inline-flex items-center px-4 py-1.5 bg-green-100 rounded-full" data-id="element-228">
                      <TrendingUp className="w-4 h-4 text-green-600 mr-1" data-id="element-229" />
                      <span className="text-sm font-bold text-green-700" data-id="element-230">
                        {t('hero.dashboard.excellent')}
                      </span>
                    </div>
                  </div>

                  {/* Metrics Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-231">
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-gray-100" data-id="element-232">
                      <div className="flex items-center gap-2 mb-2" data-id="element-233">
                        <CheckCircle2 className="w-5 h-5 text-green-500" data-id="element-234" />
                        <p className="text-xs text-gray-600 font-medium" data-id="element-235">
                          {t('hero.dashboard.negativeItems')}
                        </p>
                      </div>
                      <p className="text-2xl font-bold text-gray-900" data-id="element-236">0</p>
                      <p className="text-xs text-green-600 font-medium" data-id="element-237">
                        {t('hero.dashboard.allRemoved')}
                      </p>
                    </div>

                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-gray-100" data-id="element-238">
                      <div className="flex items-center gap-2 mb-2" data-id="element-239">
                        <CheckCircle2 className="w-5 h-5 text-green-500" data-id="element-240" />
                        <p className="text-xs text-gray-600 font-medium" data-id="element-241">
                          {t('hero.dashboard.inquiries')}
                        </p>
                      </div>
                      <p className="text-2xl font-bold text-gray-900" data-id="element-242">0</p>
                      <p className="text-xs text-green-600 font-medium" data-id="element-243">
                        {t('hero.dashboard.cleared')}
                      </p>
                    </div>
                  </div>

                  {/* Success Badges */}
                  <div className="space-y-3" data-id="element-244">
                    <div className="flex items-center gap-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-3 border border-green-100" data-id="element-245">
                      <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-246">
                        <Award className="w-6 h-6 text-white" data-id="element-247" />
                      </div>
                      <div className="flex-1" data-id="element-248">
                        <p className="text-sm font-bold text-gray-900" data-id="element-249">
                          {t('hero.dashboard.loanApproved')}
                        </p>
                        <p className="text-xs text-gray-600" data-id="element-250">
                          {t('hero.dashboard.loanAmount')}
                        </p>
                      </div>
                      <CheckCircle2 className="w-5 h-5 text-green-500" data-id="element-251" />
                    </div>

                    <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-3 border border-blue-100" data-id="element-252">
                      <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0" data-id="element-253">
                        <Award className="w-6 h-6 text-white" data-id="element-254" />
                      </div>
                      <div className="flex-1" data-id="element-255">
                        <p className="text-sm font-bold text-gray-900" data-id="element-256">
                          {t('hero.dashboard.cardApproved')}
                        </p>
                        <p className="text-xs text-gray-600" data-id="element-257">
                          {t('hero.dashboard.cardLimit')}
                        </p>
                      </div>
                      <CheckCircle2 className="w-5 h-5 text-blue-500" data-id="element-258" />
                    </div>
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-500/10 rounded-full blur-3xl" data-id="element-259" />
                <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-fuchsia-500/10 rounded-full blur-3xl" data-id="element-260" />
              </div>

              {/* Floating Badge */}
              <div className="absolute -top-4 -right-4 bg-white px-4 py-3 rounded-xl shadow-xl border border-purple-100 animate-float" data-id="element-261">
                <div className="flex items-center gap-2" data-id="element-262">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center" data-id="element-263">
                    <Shield className="w-5 h-5 text-white" data-id="element-264" />
                  </div>
                  <div data-id="element-265">
                    <p className="text-xs font-bold text-gray-900" data-id="element-266">
                      {t('hero.dashboard.protected')}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-267">
                      {t('hero.dashboard.monitoring')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}